import React, { useState, useEffect, useRef } from "react";
import * as styles from "./index.module.scss";
import AnimateIcon from "../../images/Icons/animateIcon.svg";
import { Modal, Form } from "react-bootstrap";
import get from "lodash/get";
import trim from "lodash/trim";
import { PrismicRichText } from "@prismicio/react";
import submitContactForm from "../../../utils/submitContactForm";
import GetQuote from "../../images/Icons/get_quote_email.svg";
import { format } from "date-fns";
import { navigate } from "gatsby";
// FORMS
import DefaultForm from "./FormModals/Default";
import MotorFull from "./FormModals/MotorFull";
import ThirdParty from "./FormModals/ThirdParty";
import {
  FULL_TITLE,
  THIRD_PARTY_TITLE,
  blankMotorDetails,
  isMotorTitle,
  validMotorDetails,
  makeMotorLead,
} from "./motorLead";
import Travel from "./FormModals/Travel";
import Life from "./FormModals/Life";
import Global from "./FormModals/Global";
import i18n from "../../../utils/i18n";
import DynamicForm from "./FormModals/Dynamic";
import * as PropTypes from "prop-types";
import MedicalForm from "./FormModals/Medical";
import GroupLife from "./FormModals/GroupLife";
import PersonalAccidentForm from "./FormModals/PersonalAccident";
import LiabilityForm from "./FormModals/Liability";
import SmeForm from "./FormModals/Sme";
import ProfessionalIndemnity from "./FormModals/ProfessionalIndemnity";

export const isDev = (value, defaultValue = "") =>
  process.env.NODE_ENV === "development" ? value : defaultValue;

const CSS = ({ data }) => {
  const [home, setHome] = useState(false);
  const [show, setShow] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const modalSession = useRef(0);

  const [dates, setDates] = useState({
    departure: "",
    arrival: "",
    dob: "",
  });

  const urlSearchParams =
    typeof window !== "undefined" ? window.location.search : "";

  // Valid form names that can open modals
  const validFormNames = [
    FULL_TITLE,
    THIRD_PARTY_TITLE,
    "Travel Insurance",
    "Life Insurance",
    "Global Health Insurance",
    "Offer best health and well-being to your team",
    "Give the best protection your team deserves",
    "Safety first! Be prepared for the worst",
    "Protect your business against 3rd party liabilities",
    "Best protection for your business",
    "Professional Indemnity for Doctors",
  ];

  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);
    const formTitle = params.get("pop-up-form");

    const existingBlock = data?.icon_blocks.find(
      (block) => block.modal_title === formTitle
    );

    // Check if form name is valid OR exists in Prismic
    const isValidForm = validFormNames.includes(formTitle) || existingBlock;

    if (formTitle && isValidForm) {
      setShow(true);
      setModalTitle(existingBlock?.modal_title || formTitle);
    } else {
      setShow(false);
      setModalTitle("");
    }
  }, [urlSearchParams]);

  const handleClose = () => {
    modalSession.current += 1;
    const params = new URLSearchParams(window.location.search);
    params.delete("pop-up-form");
    navigate(`${window.location.pathname}?${params.toString()}`);
  };
  const handleShow = (title = "", description = "") => {
    if (!title) return null;

    modalSession.current += 1;
    const params = new URLSearchParams(window.location.search);
    params.set("pop-up-form", title);
    navigate(`${window.location.pathname}?${params.toString()}`);
  };

  const [user, setUser] = useState({
    name: isDev("IT-Test"),
    email: isDev("it-test@gmail.com"),
    phone: isDev("0715698548"),
    company: isDev("Test Company"),
    message: isDev("Test message"),
    type: "",
    media_code: "Inme_Corporate",
    departure_date: "",
    arrival_date: "",
    date_of_birth: "",
    passport: "",
    insurance_type: "",
    product_type: "",
    profession: "",
    yearsOfExperience: "",
    coverageAmount: "",
    nic: "",
  });

  const [success, setSuccess] = useState(false);
  const [fail, setFail] = useState(false);
  const [progress, setProgress] = useState(false);
  const [motorDetails, setMotorDetails] = useState(blankMotorDetails);
  const [validationError, setValidationError] = useState("");
  const previousTitle = useRef("");
  const submitting = useRef(false);

  useEffect(() => {
    if (modalTitle !== previousTitle.current) {
      modalSession.current += 1;
    }
    if (isMotorTitle(modalTitle) || isMotorTitle(previousTitle.current)) {
      setUser((previous) => ({ ...previous, name: "", email: "", phone: "" }));
      setMotorDetails(blankMotorDetails());
      setSuccess(false);
      setFail(false);
      setValidationError("");
    }
    previousTitle.current = modalTitle;
  }, [modalTitle]);

  useEffect(() => {
    if (success || fail) {
      const timeout = setTimeout(() => {
        setSuccess(false);
        setFail(false);
      }, 6000);
      return () => clearTimeout(timeout);
    }
  }, [success, fail]);

  const handleSubmit = async (event, title) => {
    event.preventDefault();
    if (submitting.current) return;

    const motor = isMotorTitle(title);
    if (
      motor &&
      (!user.name.trim() ||
        !user.email.trim() ||
        !validMotorDetails(title, motorDetails))
    ) {
      setValidationError("Please complete all required fields.");
      return;
    }

    setValidationError("");
    setFail(false);
    setSuccess(false);
    submitting.current = true;
    const requestSession = modalSession.current;
    setProgress(true);

    const isCurrentSession = () =>
      requestSession === modalSession.current &&
      new URLSearchParams(window.location.search).get("pop-up-form") === title;

    try {
      const label = convertTitle(title);
      const baseLead = {
        ...user,
        media_code: home ? "Inme_Personal" : "Inme_Corporate",
        product_type: label,
        type: label,
        departure_date: dates.departure
          ? format(dates.departure, "yyyy-MM-dd")
          : "",
        arrival_date: dates.arrival ? format(dates.arrival, "yyyy-MM-dd") : "",
        date_of_birth: dates.dob ? format(dates.dob, "yyyy-MM-dd") : "",
      };
      const lead = motor ? makeMotorLead(title, baseLead, motorDetails) : baseLead;
      const result = await submitContactForm(lead);
      if (!isCurrentSession()) return;

      const id = get(result, "formResponse.data.inquiry_id", "");
      if (!id) {
        setFail(true);
        return;
      }

      setSuccess(true);
      setUser((previous) => ({
        ...previous,
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
        type: "",
        product_type: "",
        departure_date: "",
        arrival_date: "",
        date_of_birth: "",
        passport: "",
        insurance_type: "",
        media_code: "",
        nic: "",
      }));
      setDates({ departure: "", arrival: "", dob: "" });
      setMotorDetails(blankMotorDetails());
    } catch (error) {
      if (isCurrentSession()) setFail(true);
    } finally {
      submitting.current = false;
      setProgress(false);
    }
  };

  const convertTitle = (title = "") => {
    title = trim(title);

    const existingBlock = data?.icon_blocks.find(
      (block) => block.modal_title === title
    );

    if (!existingBlock?.description) {
      return title;
    }

    return existingBlock?.description;
  };

  useEffect(() => {
    if (window.location.pathname === i18n.t("/lang/")) {
      setHome(true);
    }
  }, []);

  return (
    <section className={styles.css_container}>
      <article className={`section-center`}>
        <div className={`${styles.css_wrapper}`}>
          <div className={`${styles.title_wrapper} ${i18n.t("locale")}`}>
            <PrismicRichText field={get(data, "feature_box_title", [])} />
            <div
              className={
                home ? styles.animate_icon_one : styles.animate_icon_two
              }
            >
              <img src={AnimateIcon} alt="Animate Icon" loading={"lazy"} />
            </div>
          </div>
          <div className={styles.package_container}>
            {get(data, "icon_blocks", []).map(
              ({ icon, description, modal_title, link }, idx) => {
                if (link) {
                  return (
                    <a className={styles.single_select} key={idx} href={link}>
                      <img src={icon.url} alt="" loading={"lazy"} />
                      <div>
                        <p className={`st-two ${i18n.t("locale")}`}>
                          {description}
                        </p>
                      </div>
                    </a>
                  );
                }
                return (
                  <button
                    type="button"
                    className={`${styles.single_select} ${styles.single_select_button}`}
                    key={idx}
                    onClick={() => {
                      handleShow(modal_title, description);
                    }}
                  >
                    <img src={icon.url} alt="" loading={"lazy"} />
                    <span className={styles.tile_description}>
                      <span className={`st-two ${i18n.t("locale")}`}>
                        {description}
                      </span>
                    </span>
                  </button>
                );
              }
            )}
          </div>
        </div>
      </article>
      <Modal
        show={show}
        onHide={handleClose}
        className={`${styles.css_form} css ${isMotorTitle(modalTitle) ? styles.motorModal : ""}`}
        centered
      >
        <Modal.Header closeButton className={styles.header}>
          <Modal.Title>
            <h3 className="st-one">{modalTitle}</h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className={styles.body}
          onClick={() => {
            setShowPopup(false);
          }}
        >
          <Form
            method="post"
            className={`${styles.css_form} forms`}
            onSubmit={(e) => handleSubmit(e, modalTitle)}
          >
            {selectForm(
              modalTitle,
              user,
              setUser,
              data,
              dates,
              setDates,
              motorDetails,
              setMotorDetails,
              showPopup,
              setShowPopup,
              styles,
              progress,
              (event) => {
                if (submitting.current) return false;
                handleSubmit(event, modalTitle);
                return true;
              }
            )}
            {validationError && (
              <div className="form-fail" role="alert">
                {validationError}
              </div>
            )}
            {success && (
              <div className="form-success">
                {" "}
                Thank you for your inquiry. We will get back to you soon.{" "}
              </div>
            )}
            {fail && (
              <div className="form-fail">
                {" "}
                Form submission failed. Please try again.{" "}
              </div>
            )}

            {modalTitle !== "Professional Indemnity for Doctors" && (
              <button
                className="primary-btn-sm"
                type="submit"
                disabled={progress}
              >
                <span>
                  <img src={GetQuote} alt="Get Quote Icon" />
                </span>
                {success ? "Submitted" : "Get in touch"}
              </button>
            )}
          </Form>
        </Modal.Body>
      </Modal>
    </section>
  );
};

const selectForm = (
  modalTitle,
  user,
  setUser,
  data,
  dates,
  setDates,
  motorDetails,
  setMotorDetails,
  showPopup,
  setShowPopup,
  styles,
  progress,
  onSubmit
) => {
  switch (modalTitle) {
    case FULL_TITLE:
      return (
        <MotorFull
          user={user}
          setUser={setUser}
          details={motorDetails}
          setDetails={setMotorDetails}
        />
      );
    case THIRD_PARTY_TITLE:
      return (
        <ThirdParty
          user={user}
          setUser={setUser}
          details={motorDetails}
          setDetails={setMotorDetails}
        />
      );
    case "Travel Insurance":
      return (
        <Travel
          user={user}
          setUser={setUser}
          showPopup={showPopup}
          setShowPopup={setShowPopup}
          dates={dates}
          setDates={setDates}
        />
      );
    case "Life Insurance":
      return <Life user={user} setUser={setUser} />;
    case "Global Health Insurance":
      return (
        <Global
          user={user}
          setUser={setUser}
          dates={dates}
          setDates={setDates}
        />
      );
    case "Offer best health and well-being to your team":
      return <MedicalForm data={data} user={user} setUser={setUser} />;
    case "Give the best protection your team deserves":
      return <GroupLife data={data} user={user} setUser={setUser} />;
    case "Safety first! Be prepared for the worst":
      return <PersonalAccidentForm data={data} user={user} setUser={setUser} />;
    case "Protect your business against 3rd party liabilities":
      return <LiabilityForm data={data} user={user} setUser={setUser} />;
    case "Best protection for your business":
      return <SmeForm data={data} user={user} setUser={setUser} />;
    case "Professional Indemnity for Doctors":
      return (
        <ProfessionalIndemnity
          user={user}
          setUser={setUser}
          onSubmit={onSubmit}
          progress={progress}
        />
      );
    default:
      return <DefaultForm data={data} user={user} setUser={setUser} />;
  }
};

export default CSS;
